/** Business time zone from the client layer (vite.config.ts `define`). */
const TZ = __OPS_PORTAL__.timezone;

const dateTimeFmt = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, dateStyle: "medium", timeStyle: "short" });
const dayFmt = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, weekday: "short", day: "numeric", month: "short" });
const longDayFmt = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric" });
const timeFmt = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true });
const ymdFmt = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
/** Date-only columns (YYYY-MM-DD) carry no zone, so format them as UTC to avoid shifting the day. */
const plainDateFmt = new Intl.DateTimeFormat("en-AU", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
const audFmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
const audWholeFmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

/** "24 Sept 2026, 7:30 am" in business time. */
export const fmtDate = (iso: string) => dateTimeFmt.format(new Date(iso));
/** "Thu 24 Sept" in business time. */
export const fmtDay = (iso: string) => dayFmt.format(new Date(iso));
/** "Thursday 24 September 2026" in business time. */
export const fmtLongDay = (iso: string | Date) => longDayFmt.format(typeof iso === "string" ? new Date(iso) : iso);
/** "5:30am" in business time. */
export const fmtTime = (iso: string) => timeFmt.format(new Date(iso)).replace(/\s/g, "").toLowerCase();
/** A date-only value such as a ticket expiry: "12 Oct 2026". */
export const fmtPlainDate = (ymd: string) => plainDateFmt.format(new Date(`${ymd}T00:00:00Z`));
/** "$1,234.50" */
export const aud = (n: number) => audFmt.format(n);
/** "$1,235" for summaries. */
export const audWhole = (n: number) => audWholeFmt.format(n);

/** Today's date in the business time zone as YYYY-MM-DD. */
export const localYmd = (d: Date = new Date()) => ymdFmt.format(d);

/** Minutes east of UTC for the business time zone at the given instant (600 or 660). */
function tzOffsetMinutes(at: Date): number {
  const name = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, timeZoneName: "longOffset" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName")?.value;
  const m = name?.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!m) return 600;
  return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
}

/** UTC instant of 00:00 business time on a YYYY-MM-DD date. */
export function localMidnight(ymd: string): Date {
  const asUtc = Date.parse(`${ymd}T00:00:00Z`);
  const guess = new Date(asUtc - tzOffsetMinutes(new Date(asUtc)) * 60_000);
  return new Date(asUtc - tzOffsetMinutes(guess) * 60_000);
}

/** Add whole days to a YYYY-MM-DD date. */
export function addDays(ymd: string, days: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86400_000).toISOString().slice(0, 10);
}

/** Whole days from Sydney today until a YYYY-MM-DD date (negative once past). */
export function daysUntil(ymd: string, today: string = localYmd()): number {
  return Math.round((Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400_000);
}

export function describeDays(days: number): string {
  if (days < 0) return days === -1 ? "Expired yesterday" : `Expired ${-days} days ago`;
  if (days === 0) return "Expires today";
  return days === 1 ? "1 day" : `${days} days`;
}

/** "public_holiday" to "Public holiday". */
export const humanise = (s: string) => {
  const t = s.replace(/[_-]+/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** UTC instant of a wall-clock time ("HH:MM") on a YYYY-MM-DD date in business time. */
export function localInstant(ymd: string, hm: string): Date {
  const asUtc = Date.parse(`${ymd}T${hm}:00Z`);
  const guess = new Date(asUtc - tzOffsetMinutes(new Date(asUtc)) * 60_000);
  return new Date(asUtc - tzOffsetMinutes(guess) * 60_000);
}

/** Day of week for a YYYY-MM-DD date: 0 = Sunday ... 6 = Saturday. */
export const weekday = (ymd: string) => new Date(`${ymd}T00:00:00Z`).getUTCDay();

/** "HH:MM" wall-clock time of an instant in business time. */
export const localHm = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));

/**
 * Pay class of a shift from its local date and start time: public holiday, weekend
 * (Saturday or Sunday), night (starting 6pm to before 5am), otherwise day.
 */
export function shiftTypeFor(ymd: string, startHm: string, holidays: readonly string[] = __OPS_PORTAL__.holidays): "day" | "night" | "weekend" | "public_holiday" {
  if (holidays.includes(ymd)) return "public_holiday";
  const dow = weekday(ymd);
  if (dow === 0 || dow === 6) return "weekend";
  const hour = Number(startHm.slice(0, 2));
  return hour >= 18 || hour < 5 ? "night" : "day";
}
